import * as React from 'react'
import { Activity, CheckCircle2, CircleSlash, Loader2, RefreshCw, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { STATUS_CHECKS as CHECKS, classify, type Check, type Result, type State } from '@/lib/status-checks'
import { Button } from '@/components/ui/button'

/**
 * Public status page — the one screen in this app that works signed out.
 *
 * ## It probes, it does not query
 *
 * Every check here is a plain `fetch` from the visitor's browser to a health
 * endpoint on this same origin. Nothing reads the database, and nothing needs a
 * token: `anon` is granted nothing in this schema (see AGENTS.md), and a status
 * page is not a good enough reason to open the first unauthenticated read path
 * in the tenant. What it can honestly report is therefore "does each service
 * answer", which is also what an outage looks like from outside.
 *
 * Because the checks run in the visitor's browser rather than on a server, this
 * page reports what THEY can reach. That is a feature: a green page on someone
 * else's laptop while yours shows red is real information about the network
 * between you, not a bug in the page.
 *
 * ## Why each check carries its own list of acceptable codes
 *
 * Healthy does not mean 200. Realtime answers `403` to an anonymous request for
 * a tenant, and the edge-function runtime answers `405` to a GET — in both
 * cases the service is up and refusing politely, which is exactly what we want
 * to see. Measured against the running tenant rather than assumed; a `curl` of
 * each path is in `scripts/status-check.mjs`, which fails if these lists ever
 * stop matching reality.
 */

const TIMEOUT_MS = 6000

async function probe(check: Check): Promise<Result> {
  const started = performance.now()
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS)
  try {
    const res = await fetch(check.path, { cache: 'no-store', signal: controller.signal })
    // Only read the body when something actually depends on it — these are
    // health endpoints, but the SPA index is 30KB and the deck index larger.
    const body = check.needle ? await res.text() : null
    const out = classify(check, res.status, body)
    return { ...out, ms: Math.round(performance.now() - started) }
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError'
    return {
      state: 'down',
      ms: Math.round(performance.now() - started),
      note: aborted ? `No answer in ${TIMEOUT_MS / 1000}s` : 'Could not be reached',
    }
  } finally {
    window.clearTimeout(timer)
  }
}

const REFRESH_MS = 30_000

export function StatusPage() {
  const [results, setResults] = React.useState<Record<string, Result>>({})
  const [checkedAt, setCheckedAt] = React.useState<Date | null>(null)
  const [running, setRunning] = React.useState(false)

  const runAll = React.useCallback(async () => {
    setRunning(true)
    setResults((prev) => {
      const next = { ...prev }
      // Keep the last known state visible while re-checking: blanking the board
      // every 30 seconds makes a healthy system look like it keeps dropping.
      for (const c of CHECKS) if (!next[c.id]) next[c.id] = { state: 'checking' }
      return next
    })
    const done = await Promise.all(CHECKS.map(async (c) => [c.id, await probe(c)] as const))
    setResults(Object.fromEntries(done))
    setCheckedAt(new Date())
    setRunning(false)
  }, [])

  React.useEffect(() => {
    void runAll()
    const t = window.setInterval(() => void runAll(), REFRESH_MS)
    return () => window.clearInterval(t)
  }, [runAll])

  const states = CHECKS.map((c) => results[c.id]?.state ?? 'checking')
  const down = states.filter((s) => s === 'down').length
  const settled = states.every((s) => s !== 'checking')

  const overall = !settled
    ? { label: 'Checking…', tone: 'muted' as const }
    : down === 0
      ? { label: 'All systems operational', tone: 'up' as const }
      : down === states.length
        ? { label: 'Everything is down from here', tone: 'down' as const }
        : { label: `${down} of ${states.length} services are down`, tone: 'down' as const }

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-8 flex items-center gap-3">
          <img
            src="https://dontpaniclabs.com/wp-content/uploads/2023/06/DPL-full-color-2-o.svg"
            alt="Don't Panic Labs"
            className="h-9"
          />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-tight">Team Accelerator status</h1>
            <p className="text-xs text-muted-foreground">
              Live checks from this browser. No sign-in, no team data.
            </p>
          </div>
        </header>

        <div
          className={cn(
            'mb-6 flex items-center gap-3 rounded-lg border p-4',
            overall.tone === 'up' && 'border-primary/40 bg-primary/5',
            overall.tone === 'down' && 'border-destructive/40 bg-destructive/5',
          )}
        >
          {overall.tone === 'up' ? (
            <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
          ) : overall.tone === 'down' ? (
            <XCircle className="h-5 w-5 shrink-0 text-destructive" />
          ) : (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{overall.label}</div>
            <div className="text-xs text-muted-foreground">
              {checkedAt
                ? `Checked ${checkedAt.toLocaleTimeString()} · rechecks every ${REFRESH_MS / 1000}s`
                : 'Running the first check…'}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => void runAll()} disabled={running}>
            <RefreshCw className={cn(running && 'animate-spin')} /> Check now
          </Button>
        </div>

        <ul className="space-y-2">
          {CHECKS.map((c) => {
            const r = results[c.id] ?? { state: 'checking' as const }
            return (
              <li
                key={c.id}
                className="flex items-start gap-3 rounded-lg border bg-card px-4 py-3"
              >
                <StateIcon state={r.state} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-tight">{c.label}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{c.detail}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div
                    className={cn(
                      'text-xs font-medium',
                      r.state === 'up' && 'text-primary',
                      r.state === 'down' && 'text-destructive',
                      (r.state === 'absent' || r.state === 'checking') && 'text-muted-foreground',
                    )}
                  >
                    {r.state === 'up'
                      ? 'Operational'
                      : r.state === 'down'
                        ? 'Not responding'
                        : r.state === 'absent'
                          ? 'Not deployed'
                          : 'Checking…'}
                  </div>
                  <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                    {r.note ?? (r.status ? `HTTP ${r.status}` : '')}
                    {r.ms != null && ` · ${r.ms} ms`}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>

        <p className="mt-6 text-xs text-muted-foreground">
          This page asks each service whether it is answering. It reads nothing from the database and
          shows nothing about any team — everything in the portal itself still requires a sign-in.
        </p>
      </div>
    </div>
  )
}

function StateIcon({ state }: { state: State }) {
  if (state === 'up') return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
  if (state === 'down') return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
  if (state === 'absent') return <CircleSlash className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
  return <Activity className="mt-0.5 h-4 w-4 shrink-0 animate-pulse text-muted-foreground" />
}
